import { MoreVert } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FeatureCollection } from "@turf/turf";
import { useNotification } from "app/NotificationProvider";
import { analytics } from "common/analytics";
import { AnalyticsEvents } from "common/analytics/events";
import { BottomSheetHeader, PageTitleBar } from "common/components";
import { HectaresIcon, SwardCompositionIcon } from "common/icons";
import { useTranslation } from "common/locales";
import { FieldEditActionMenu } from "features/fieldDetail";
import AfterFieldCreatedDialog from "features/fieldEdit/AfterFieldCreatedDialog";
import ArableTypeDropdown from "features/fieldEdit/ArableTypeDropdown";
import {
  ARABLE_TYPE,
  ArableField,
  FIELD_TYPE,
  getArableFieldsFilteredFromArray,
  getGrainFieldsFromArray,
  useArableFields,
} from "model";
import { useCurrentFarm } from "model/useFarmQueries";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { updateArableField } from "services";
import { useUIStore } from "stores";

interface Props {
  field: ArableField;
}

interface LocationState {
  previousPath?: string;
  previousSearch?: string;
}

export default function ArableFieldDetails({ field }: Props) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation<LocationState>();
  const { data: currentFarm } = useCurrentFarm();
  const setZoomToGeom = useUIStore((state) => state.setZoomToGeom);

  const { notify } = useNotification();

  // Get field lists for navigation logic
  const { data: allArableFields = [] } = useArableFields();
  const grainFields = useMemo(
    () => getGrainFieldsFromArray(allArableFields),
    [allArableFields]
  );
  const arableFields = useMemo(
    () => getArableFieldsFilteredFromArray(allArableFields),
    [allArableFields]
  );

  const fieldGeom = useMemo(
    (): FeatureCollection => ({
      type: "FeatureCollection",
      features: [field.geom],
    }),
    [field.geom]
  );

  // Track which field we've zoomed to, so we only zoom once per field
  const zoomedToFieldRef = useRef<string | null>(null);

  useEffect(() => {
    // Only zoom if we haven't already zoomed to this field
    if (zoomedToFieldRef.current !== field.guid) {
      zoomedToFieldRef.current = field.guid;
      setZoomToGeom(fieldGeom);
      analytics.track(AnalyticsEvents.FIELD_VIEWED, {
        fieldGuid: field.guid,
        fieldType: "arable",
      });
    }
  }, [field.guid, fieldGeom, setZoomToGeom]);

  async function handleArableTypeChange(
    value: ARABLE_TYPE | undefined,
    field: ArableField
  ) {
    if (value && field) {
      try {
        await updateArableField({
          ...field,
          arableType: value,
        });
        analytics.track(AnalyticsEvents.FIELD_ARABLE_TYPE_UPDATED, {
          fieldGuid: field.guid,
          grassType: value,
        });
      } catch (e) {
        console.error("Failed to update grass type", e);
        notify(t("fieldinput.grassType.updateFailed"), "error");
      }
    }
  }

  const handleBack = () => {
    // If we have a previous path, use it
    if (location.state?.previousPath) {
      navigate({
        pathname: location.state.previousPath,
        search: location.state.previousSearch || "",
      });
      return;
    }

    // Determine field type and navigate to appropriate fields list
    const isGrainField = grainFields.some((f) => f?.guid === field.guid);
    if (isGrainField) {
      navigate("/fields-list/grain");
      return;
    }

    const isArableField = arableFields.some((f) => f?.guid === field.guid);
    if (isArableField) {
      navigate("/fields-list/arable");
      return;
    }

    // Fallback to farm view if no previous path
    backToFarm();
  };

  const backToFarm = () => {
    if (currentFarm?.guid) {
      navigate(`/farm/${currentFarm.guid}`);
    }
  };

  const iconSize = "medium";

  // Determine field type prefix
  const isGrainField = grainFields.some((f) => f?.guid === field.guid);

  const getFieldTypePrefix = () => {
    if (isGrainField) {
      return t("field.prefix.grain");
    }
    return t("field.prefix.arable");
  };

  const fieldTypePrefix = getFieldTypePrefix();

  // Header right actions
  const headerRightAction = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <IconButton
        onClick={openMenu}
        size="small"
        sx={{
          backgroundColor: "#F1EFF7",
          color: "#7D5DA3",
          "&:hover": { backgroundColor: "#E5E1F0" },
        }}
      >
        <MoreVert />
      </IconButton>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <BottomSheetHeader
          titlePrefix={`${fieldTypePrefix}:`}
          title={field.name}
          onBack={handleBack}
          rightAction={headerRightAction}
        />
      ) : (
        <PageTitleBar
          titlePrefix={`${fieldTypePrefix}:`}
          title={field.name}
          onBack={handleBack}
        >
          {headerRightAction}
        </PageTitleBar>
      )}

      {/* Main Content */}
      <Box sx={{ pb: 2, backgroundColor: "white" }}>
        <Box
          sx={{
            backgroundColor: "white",
            px: 3,
          }}
        >
          {/* Field Metrics Section */}
          <Box sx={{ pb: 3 }}>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: "text.primary",
                paddingTop: 4,
              }}
            >
              {t("fieldDetail.title")}
            </Typography>

            <Stack spacing={2}>
              {/* Area */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <HectaresIcon fontSize={iconSize} htmlColor="#1C4321" />
                  </Box>
                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 500, color: "black" }}
                    >
                      {field ? `${(field.area / 10000).toFixed(2)} ha` : ""}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "black", fontWeight: 400 }}
                    >
                      {t("fieldDetail.area")}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Divider after Area */}
              <Divider />

              {/* Arable Type */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <SwardCompositionIcon />
                  </Box>
                  <Box>
                    {!field.arableType ? (
                      <ArableTypeDropdown
                        disabled={false}
                        onChange={async (value?: ARABLE_TYPE) => {
                          await handleArableTypeChange(value, field);
                        }}
                        currentArableType={field.arableType}
                        arableTypeInputError={false}
                        label={"fieldinput.arableType.text"}
                      />
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, color: "black" }}
                      >
                        {t(`fieldinput.arableType.option.${field.arableType}`)}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ color: "black", fontWeight: 400 }}
                    >
                      {t("fieldinput.arableType.text")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>

      <FieldEditActionMenu
        anchor={menuAnchor}
        close={closeMenu}
        fieldId={field.guid || ""}
        fieldType={FIELD_TYPE.ARABLE}
      />
      <AfterFieldCreatedDialog />
    </>
  );

  function openMenu(event: React.MouseEvent<HTMLButtonElement>) {
    setMenuAnchor(event.currentTarget);
  }

  function closeMenu() {
    setMenuAnchor(null);
  }
}
